// Console delegation V1.0; 2026-10-07 09:05:47 CST; parent dcebd36069b2ddb3e45e586f2c90a0214b9a2a4f.
// HotX Console 0.1.121 域名管理默认首页。实际修改时间：2026-09-29 13:25:25 CST；精确父基线：2b265b8475709018e4bf7099ec6a081871eb4c6b；变更：CONSOLE-HOME-DATA-PASSWORD-20260929。
/* HotX 域名路由注册备份注释
 * 注释时间：2026-08-29 20:52 CST
 * 域名管理通过 /domains 和 /domains/new 注册，保持登录保护、面包屑和脏状态检查。
 */

/* HotX 证书路由注册备份注释
 * 注释时间：2026-08-29 20:52 CST
 * 证书管理通过 freeCertificateRoute 注册到 /https，保持登录保护和页面脏状态检查。
 */

import { createRouter, createWebHistory, type Router, type RouterHistory } from 'vue-router';
import DashboardView from '../views/DashboardView.vue';
import LoginView from '../views/LoginView.vue';
import DelegationView from '../views/DelegationView.vue';
import { inDelegationContext } from '../auth/delegationSession';
import DomainsView from '../views/DomainsView.vue';
import OriginsView from '../views/OriginsView.vue';
import { freeCertificateRoute } from '../modules/free-certificate';
import CacheView from '../views/CacheView.vue';
import SecurityView from '../views/SecurityView.vue';
import TrafficView from '../views/TrafficView.vue';
import IpCollectionsView from '../views/IpCollectionsView.vue';
// 2026-09-05：策略页面切换到“策略为主对象”的共享工作区，其它路由保持不变。
import PoliciesView from '../views/StrategyPoliciesView.vue';
import PackagesView from '../views/PackagesView.vue';
import PointsView from '../views/PointsView.vue';
import RechargeView from '../views/RechargeView.vue';
import BscRechargeView from '../views/BscRechargeView.vue';
import NotificationsView from '../views/NotificationsView.vue';
import LogsView from '../views/LogsView.vue';
import AccountView from '../views/AccountView.vue';
import NotFoundView from '../views/NotFoundView.vue';
import { clearConsoleNavigationDirty, isConsoleNavigationDirty } from './navigationState';

export interface GuardTokenReader {
  getAccessToken(): string | null;
  getMustChangePassword?(): boolean;
  clear?(): void;
}

export interface ConsoleSessionGuard {
  ensureCurrentCustomer(): Promise<boolean>;
}

export interface ConsoleRouterOptions {
  uatIpBypassEnabled?: boolean;
  sessionGuard?: ConsoleSessionGuard;
}

export function normalizeConsoleRedirect(value: unknown): string {
  const redirect = Array.isArray(value) ? value[0] : value;
  if (typeof redirect !== 'string' || !redirect.startsWith('/')) {
    return '/domains';
  }
  if (redirect.startsWith('//') || redirect.includes('://') || redirect.toLowerCase().startsWith('/javascript:')) {
    return '/domains';
  }
  return redirect;
}

export function createConsoleRouter(
  tokenReader: GuardTokenReader,
  history?: RouterHistory,
  options: ConsoleRouterOptions = {}
): Router {
  const router = createRouter({
    history: history ?? createWebHistory(),
    routes: [
      { path: '/delegation', component: DelegationView },
      { path: '/delegation-ended', component: DelegationView },
      {
        path: '/login',
        name: 'login',
        component: LoginView,
        beforeEnter: (to) => {
          // 更新时间：2026-09-04 13:08 CST。普通登录链接直接进入游客控制台，只为弹窗认证和 OAuth 回调保留登录组件。
          const isPopupAuthentication = to.query.popup === '1';
          const isGoogleCallback = typeof to.query.state === 'string'
            && (typeof to.query.code === 'string' || typeof to.query.error === 'string');
          if (isPopupAuthentication || isGoogleCallback) return true;

          const rawRedirect = Array.isArray(to.query.redirect) ? to.query.redirect[0] : to.query.redirect;
          const guestTarget = typeof rawRedirect === 'string'
            && rawRedirect.startsWith('/')
            && !rawRedirect.startsWith('//')
            && !/^\/login(?:[/?#]|$)/.test(rawRedirect)
            ? rawRedirect
            : '/domains';
          return guestTarget;
        }
      },
      { path: '/', redirect: '/domains' },
      { path: '/home', redirect: '/domains' },
      { path: '/dashboard', name: 'dashboard', component: DashboardView, meta: { requiresAuth: true, breadcrumb: [] } },
      // V1.0 (2026-09-10): /domains contains the inline creation workbench, so it
      // needs the same leave guard as /domains/new before sign-in navigation.
      { path: '/domains', name: 'domains', component: DomainsView, meta: { requiresAuth: true, breadcrumb: ['域名管理'], dirtyCheck: true } },
      { path: '/domains/new', name: 'domain-new', component: DomainsView, meta: { requiresAuth: true, breadcrumb: ['域名管理', '添加域名'], dirtyCheck: true } },
      { path: '/origins', name: 'origins', component: OriginsView, meta: { requiresAuth: true, breadcrumb: ['源站管理'], dirtyCheck: true } },
      freeCertificateRoute,
      { path: '/cache', name: 'cache', component: CacheView, meta: { requiresAuth: true, breadcrumb: ['刷新缓存'], dirtyCheck: true } },
      { path: '/traffic', name: 'traffic', component: TrafficView, meta: { requiresAuth: true, breadcrumb: ['流量监控'] } },
      { path: '/ip-collections', name: 'ip-collections', component: IpCollectionsView, meta: { requiresAuth: true, breadcrumb: ['IP 集合'], dirtyCheck: true } },
      { path: '/policies', name: 'policies', component: PoliciesView, meta: { requiresAuth: true, breadcrumb: ['策略管理'], dirtyCheck: true } },
      { path: '/packages', name: 'packages', component: PackagesView, meta: { requiresAuth: true, breadcrumb: ['套餐管理'] } },
      { path: '/points', name: 'points', component: PointsView, meta: { requiresAuth: true, breadcrumb: ['积分中心'] } },
      // 2026-09-03 CST：TRX 充值升级为独立正式页面；旧地址保留无损跳转，兼容历史书签和已有链接。
      { path: '/recharge', redirect: (to) => ({ path: '/trx-recharge', query: to.query }) },
      // 2026-09-03 CST：正式入口复用原 RechargeView，只调整页面归属，不改变已稳定的 TRON 充值业务逻辑。
      { path: '/trx-recharge', name: 'trx-recharge', component: RechargeView, meta: { requiresAuth: true, breadcrumb: ['积分中心', 'TRX 充值'] } },
      { path: '/bsc-recharge', name: 'bsc-recharge', component: BscRechargeView, meta: { requiresAuth: true, breadcrumb: ['积分中心', 'BSC 充值'] } },
      { path: '/notifications', name: 'notifications', component: NotificationsView, meta: { requiresAuth: true, breadcrumb: ['消息中心'] } },
      { path: '/security', name: 'security', component: SecurityView, meta: { requiresAuth: true, breadcrumb: ['安全防护'], dirtyCheck: true } },
      { path: '/publish', redirect: '/domains' },
      { path: '/logs', name: 'logs', component: LogsView, meta: { requiresAuth: true, breadcrumb: ['日志服务'] } },
      { path: '/account', name: 'account', component: AccountView, meta: { requiresAuth: true, breadcrumb: ['账户中心'] } },
      { path: '/:pathMatch(.*)*', name: 'console-fallback', component: NotFoundView, meta: { requiresAuth: true, breadcrumb: ['页面不存在'] } }
    ]
  });

  router.beforeEach((to, from) => {
    if (from.meta.dirtyCheck && isConsoleNavigationDirty()) {
      const leave = window.confirm('当前页面有未保存内容，确定离开吗？');
      if (!leave) {
        return false;
      }
      clearConsoleNavigationDirty();
    }
    const publicRoute = ['/login', '/delegation', '/delegation-ended'].includes(to.path);
    const hasAccessToken = Boolean(tokenReader.getAccessToken());
    // 0.1.121：已有首次改密令牌刷新或深链访问时，也必须先进入账户中心；不能只依赖登录页跳转。
    if (!publicRoute && to.path !== '/account' && hasAccessToken && tokenReader.getMustChangePassword?.()) {
      return { path: '/account', replace: true };
    }
    // 更新时间：2026-09-04 11:34 CST。无令牌时直接进入游客参观模式，不再跳转登录页。
    if (!publicRoute && hasAccessToken && options.sessionGuard && !options.uatIpBypassEnabled) {
      return options.sessionGuard.ensureCurrentCustomer().then(validSession => {
        if (!validSession) {
          tokenReader.clear?.();
          if (inDelegationContext()) return { path: '/delegation-ended', replace: true };
          return true;
        }
        return true;
      });
    }
    return true;
  });

  return router;
}
